import api from "../axios";

// Project HSE handbook (HMS Håndbok for prosjekt)
// Backend: /api/v1/projects/:project_id/hse_handbook/*

/**
 * Fetch the full handbook for a project: curated entries, crew list and meta.
 * @param {number|string} projectId
 */
export const getProjectHandbook = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/hse_handbook`);
  return response.data;
};

/**
 * List company HSE library documents that can be added to the handbook.
 * @param {number|string} projectId
 * @param {Object} params - { discipline_id, category_id }
 */
export const getAvailableHandbookDocuments = async (projectId, params = {}) => {
  const response = await api.get(`/projects/${projectId}/hse_handbook/available_documents`, { params });
  return response.data;
};

/**
 * Fetch full content for a single handbook entry (works for company refs and project copies).
 * @param {number|string} projectId
 * @param {number|string} phdId - project_hse_document id
 */
export const getHandbookDocument = async (projectId, phdId) => {
  const response = await api.get(`/projects/${projectId}/hse_handbook/documents/${phdId}`);
  return response.data;
};

/**
 * Add company documents (by id) as handbook references.
 * @param {number|string} projectId
 * @param {Array<number>} documentIds
 */
export const addHandbookDocuments = async (projectId, documentIds) => {
  const response = await api.post(`/projects/${projectId}/hse_handbook/documents`, {
    document_ids: documentIds,
  });
  return response.data;
};

/**
 * Bulk import handbook references from the company HSE library.
 * @param {number|string} projectId
 * @param {Object} params - { discipline_id, category_id, document_type, include_forms }
 */
export const importHandbookDocuments = async (projectId, params = {}) => {
  const response = await api.post(`/projects/${projectId}/hse_handbook/import`, params);
  return response.data;
};

/**
 * Update a single handbook entry (sort_order / included).
 */
export const updateHandbookDocument = async (projectId, phdId, payload = {}) => {
  const response = await api.patch(`/projects/${projectId}/hse_handbook/documents/${phdId}`, payload);
  return response.data;
};

/**
 * Reorder handbook entries.
 * @param {Array<number>} order - ordered array of project_hse_document ids
 */
export const reorderHandbookDocuments = async (projectId, order) => {
  const response = await api.post(`/projects/${projectId}/hse_handbook/reorder`, { order });
  return response.data;
};

/**
 * Remove a handbook entry (also deletes the project copy if it was an override).
 */
export const removeHandbookDocument = async (projectId, phdId) => {
  const response = await api.delete(`/projects/${projectId}/hse_handbook/documents/${phdId}`);
  return response.data;
};

/**
 * Create an editable project-specific copy (override) of a referenced company document.
 */
export const createHandbookOverride = async (projectId, phdId) => {
  const response = await api.post(`/projects/${projectId}/hse_handbook/documents/${phdId}/override`);
  return response.data;
};

/**
 * Generate the project handbook PDF (returns { pdf_url }).
 */
export const printProjectHandbook = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/hse_handbook/print`, {
    params: { printType: "book" },
  });
  return response.data;
};
